import React, { useEffect, useState } from 'react';
import api from '../../api/client';
import {
  Calendar, Users, UserPlus, Briefcase, AlertTriangle, TrendingUp,
  BarChart2, Clock, Star, Activity, ArrowLeft
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';

const COLORS = ['#2575fc', '#6a11cb', '#10b981', '#f59e0b', '#ef4444', '#06b6d4'];

const DailyDigest: React.FC = () => {
  const navigate = useNavigate();
  const [digest, setDigest] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await api.get('/recruitment-analytics/daily-digest', { params: { date } });
        setDigest(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [date]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', color: '#64748b' }}>
        <Clock size={32} style={{ animation: 'spin 1s linear infinite', marginRight: 12 }} />
        Загрузка дайджеста...
        <style>{`@keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!digest) {
    return (
      <div style={{ ...styles.glassCard, margin: '40px auto', maxWidth: 600, textAlign: 'center', padding: 60 }}>
        <Calendar size={48} color="#94a3b8" style={{ marginBottom: 16 }} />
        <h2 style={{ color: '#1a1a2e', marginBottom: 8 }}>Нет данных за выбранную дату</h2>
        <p style={{ color: '#64748b', marginBottom: 24 }}>Попробуйте выбрать другой день или вернитесь к дашборду.</p>
        <button onClick={() => navigate('/app/recruitment-analytics')} style={styles.linkButton}>
          <ArrowLeft size={16} /> К дашборду
        </button>
      </div>
    );
  }

  const responseColor = digest.avgResponseHours < 4 ? '#10b981' : digest.avgResponseHours < 24 ? '#f59e0b' : '#ef4444';

  return (
    <div style={{ padding: '0 20px 40px 20px', fontFamily: 'Inter, sans-serif' }}>
      
      {/* Шапка с выбором даты */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 48, height: 48, borderRadius: 14, background: 'linear-gradient(135deg, #6a11cb, #2575fc)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', boxShadow: '0 8px 20px rgba(106,17,203,0.3)' }}>
            <Calendar size={24} />
          </div>
          <div>
            <h1 style={{ fontSize: 28, fontWeight: 800, color: '#1a1a2e', margin: 0 }}>Ежедневный дайджест</h1>
            <p style={{ color: '#64748b', margin: '4px 0 0 0', fontSize: 14 }}>Сводка событий за выбранный день</p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={{ padding: '10px 16px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.6)', background: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(10px)', fontSize: 14, fontWeight: 500, color: '#1a1a2e' }}
          />
          <button onClick={() => navigate('/app/recruitment-analytics')} style={styles.linkButton}>
            <ArrowLeft size={16} /> Назад
          </button>
        </div>
      </div>

      {/* KPI-карточки */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiIcon, background: 'rgba(37,117,252,0.1)', color: '#2575fc' }}><Users size={22} /></div>
          <div style={styles.kpiLabel}>Новых кандидатов</div>
          <div style={styles.kpiValue}>{digest.newCandidatesCount}</div>
        </div>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiIcon, background: 'rgba(106,17,203,0.1)', color: '#6a11cb' }}><UserPlus size={22} /></div>
          <div style={styles.kpiLabel}>Перемещено</div>
          <div style={styles.kpiValue}>{digest.movedCandidatesCount}</div>
        </div>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiIcon, background: 'rgba(16,185,129,0.1)', color: '#10b981' }}><Briefcase size={22} /></div>
          <div style={styles.kpiLabel}>Закрыто вакансий</div>
          <div style={styles.kpiValue}>{digest.closedVacanciesCount}</div>
        </div>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiIcon, background: 'rgba(245,158,11,0.1)', color: '#f59e0b' }}><AlertTriangle size={22} /></div>
          <div style={styles.kpiLabel}>Долгих вакансий</div>
          <div style={styles.kpiValue}>{digest.staleVacanciesCount}</div>
        </div>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiIcon, background: `${responseColor}15`, color: responseColor }}><Clock size={22} /></div>
          <div style={styles.kpiLabel}>Ср. время отклика</div>
          <div style={{ ...styles.kpiValue, color: responseColor }}>{digest.avgResponseHours} ч</div>
        </div>
      </div>

      {/* Алерты */}
      {digest.alerts?.length > 0 && (
        <div style={{ ...styles.glassCard, borderLeft: '4px solid #ef4444', marginBottom: 24, padding: '16px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <AlertTriangle size={20} color="#ef4444" />
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#ef4444' }}>Требует внимания</h3>
          </div>
          <ul style={{ margin: 0, paddingLeft: 20, color: '#4a4a68' }}>
            {digest.alerts.map((a: any, i: number) => (
              <li key={i} style={{ marginBottom: 6 }}>{a.message}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Графики */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
        <div style={styles.glassCard}>
          <h3 style={styles.sectionTitle}><Activity size={18} /> Новые кандидаты по источникам</h3>
          <div style={{ height: 260 }}>
            {digest.sourceBreakdown?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={digest.sourceBreakdown} dataKey="count" nameKey="name" innerRadius={60} outerRadius={90} paddingAngle={4}>
                    {digest.sourceBreakdown.map((_: any, idx: number) => (
                      <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(10px)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.5)' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#94a3b8' }}>Нет новых кандидатов</div>
            )}
          </div>
        </div>

        <div style={styles.glassCard}>
          <h3 style={styles.sectionTitle}><TrendingUp size={18} /> Перемещения по этапам</h3>
          <div style={{ height: 260 }}>
            {digest.stageMoves?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={digest.stageMoves} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="stageGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#6a11cb" />
                      <stop offset="100%" stopColor="#2575fc" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" vertical={false} />
                  <XAxis dataKey="stage" tick={{ fontSize: 11, fill: '#64748b' }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                  <Tooltip contentStyle={{ background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(10px)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.5)' }} />
                  <Bar dataKey="count" fill="url(#stageGradient)" radius={[8, 8, 0, 0]} animationDuration={1000} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#94a3b8' }}>Нет перемещений</div>
            )}
          </div>
        </div>
      </div>

      {/* Топ рекрутеров */}
      <div style={styles.glassCard}>
        <h3 style={styles.sectionTitle}><Star size={18} /> Топ-рекрутеры дня</h3>
        {digest.topRecruiters?.length > 0 ? (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,0.05)', color: '#64748b', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Рекрутер</th>
                <th style={{ textAlign: 'right', padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,0.05)', color: '#64748b', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Перемещений</th>
              </tr>
            </thead>
            <tbody>
              {digest.topRecruiters.map((r: any, i: number) => (
                <tr key={i} style={{ borderBottom: '1px solid rgba(0,0,0,0.03)' }}>
                  <td style={{ padding: '14px 16px', fontSize: 14, fontWeight: 500, color: '#1a1a2e' }}>{r.name}</td>
                  <td style={{ padding: '14px 16px', textAlign: 'right', fontSize: 14, fontWeight: 700, color: '#2575fc' }}>{r.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p style={{ color: '#94a3b8', textAlign: 'center', padding: 20 }}>Нет данных</p>
        )}
      </div>

      {/* Проблемные вакансии */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginTop: 24 }}>
        <div style={styles.glassCard}>
          <h3 style={styles.sectionTitle}><AlertTriangle size={18} /> Долго открытые ({digest.staleVacanciesCount})</h3>
          {digest.staleVacancyTitles?.length > 0 ? (
            <ul style={{ paddingLeft: 20, margin: 0 }}>
              {digest.staleVacancyTitles.map((t: string, i: number) => (
                <li key={i} style={{ marginBottom: 8, color: '#4a4a68', fontSize: 14 }}>{t}</li>
              ))}
            </ul>
          ) : (
            <p style={{ color: '#94a3b8', textAlign: 'center', padding: 20 }}>Нет долго открытых</p>
          )}
        </div>
        <div style={styles.glassCard}>
          <h3 style={styles.sectionTitle}><Users size={18} /> Без кандидатов ({digest.emptyVacanciesCount})</h3>
          {digest.emptyVacancyTitles?.length > 0 ? (
            <ul style={{ paddingLeft: 20, margin: 0 }}>
              {digest.emptyVacancyTitles.map((t: string, i: number) => (
                <li key={i} style={{ marginBottom: 8, color: '#4a4a68', fontSize: 14 }}>{t}</li>
              ))}
            </ul>
          ) : (
            <p style={{ color: '#94a3b8', textAlign: 'center', padding: 20 }}>Все вакансии имеют кандидатов</p>
          )}
        </div>
      </div>

    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  glassCard: { background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderRadius: 24, padding: 24, border: '1px solid rgba(255,255,255,0.6)', boxShadow: '0 12px 40px rgba(31,38,135,0.06)', marginBottom: 24 },
  kpiCard: { background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderRadius: 20, padding: '20px 16px', border: '1px solid rgba(255,255,255,0.6)', boxShadow: '0 8px 24px rgba(31,38,135,0.04)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, textAlign: 'center' },
  kpiIcon: { width: 44, height: 44, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 4 },
  kpiLabel: { fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' },
  kpiValue: { fontSize: 26, fontWeight: 800, color: '#1a1a2e' },
  sectionTitle: { fontSize: 18, fontWeight: 700, color: '#1a1a2e', margin: '0 0 20px 0', display: 'flex', alignItems: 'center', gap: 8 },
  linkButton: { background: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(10px)', border: '1px solid #2575fc', color: '#2575fc', borderRadius: 30, padding: '10px 20px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 14 },
};

export default DailyDigest;
